import { formatTarget, type ClientTarget } from '@features/clients';
import { ApiError, UnauthenticatedError } from '@lib/api';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import {
    getBulkJob,
    launchBulkMigration,
    listBulkJobs,
    listBulkModules,
    refreshBulkLaunch,
    startBulkDryRun,
    type DryRunInput,
} from '../api/bulk-migrations';
import { autoRefreshDue, isActive, jobPollInterval } from '../model/job-progress';
import { FINAL_REPORT_VIEW } from '../model/navigation';
import { collectAllRows, FULL_READ_PAGE_SIZE } from '../model/paging';

import type { JobHeader, TenantRow } from '../schemas/bulk-migration';

/**
 * Server state for the Salesforce migration screen. The target is in every key,
 * for the reason `use-clients.ts` gives: two environments never share a cache.
 */

export const bulkKeys = {
    all: ['bulk-migrations'] as const,
    modules: (target: string) => [...bulkKeys.all, 'modules', target] as const,
    jobs: (target: string, moduleId: string) =>
        [...bulkKeys.all, 'jobs', target, moduleId] as const,
    /** Under `jobs`, so invalidating a module's jobs refreshes every window of them. */
    jobsWindow: (target: string, moduleId: string, limit: number) =>
        [...bulkKeys.jobs(target, moduleId), limit] as const,
    job: (target: string, moduleId: string, reportId: string) =>
        [...bulkKeys.all, 'job', target, moduleId, reportId] as const,
    rows: (target: string, moduleId: string, reportId: string, outcome: string) =>
        [...bulkKeys.all, 'rows', target, moduleId, reportId, outcome] as const,
};

/** Rows per page in the table. */
export const TABLE_PAGE_SIZE = 50;

export function isMissing(error: unknown): boolean {
    return error instanceof ApiError && error.status === 404;
}

/** A 4xx is an answer, not a blip — the policy every screen here uses. */
export function retryUnlessClientError(failureCount: number, error: Error): boolean {
    if (error instanceof UnauthenticatedError) return false;
    if (error instanceof ApiError && error.status < 500) return false;
    return failureCount < 1;
}

export function useBulkModules(target: ClientTarget) {
    const id = formatTarget(target);
    return useQuery({
        queryKey: bulkKeys.modules(id ?? 'unaddressable'),
        queryFn: ({ signal }) => listBulkModules(target, signal),
        enabled: id !== null,
        retry: retryUnlessClientError,
    });
}

/**
 * A module's recent jobs (the newest `limit`). Re-read every 15 s while any of
 * them is still active.
 */
export function useBulkJobs(target: ClientTarget, moduleId: string | null, limit = 20) {
    const id = formatTarget(target);
    return useQuery({
        queryKey: bulkKeys.jobsWindow(id ?? 'unaddressable', moduleId ?? '', limit),
        queryFn: ({ signal }) => listBulkJobs(target, moduleId ?? '', signal, limit),
        enabled: id !== null && moduleId !== null,
        retry: retryUnlessClientError,
        refetchInterval: (query) =>
            query.state.data?.some((job) => isActive(job.state)) === true ? 15_000 : false,
    });
}

/**
 * One job's header, polled while it is active (`jobPollInterval`). Asks for one
 * row only: the header is what moves, and the table reads its rows separately.
 */
export function useBulkJob(target: ClientTarget, moduleId: string | null, reportId: string | null) {
    const id = formatTarget(target);
    return useQuery({
        queryKey: bulkKeys.job(id ?? 'unaddressable', moduleId ?? '', reportId ?? ''),
        queryFn: async ({ signal }) =>
            (await getBulkJob(target, moduleId ?? '', reportId ?? '', { limit: 1 }, signal)).job,
        enabled: id !== null && moduleId !== null && reportId !== null,
        retry: (failureCount, error) => !isMissing(error) && failureCount < 1,
        refetchInterval: (query) =>
            jobPollInterval(
                query.state.data,
                query.state.error === null
                    ? 'none'
                    : isMissing(query.state.error)
                      ? 'missing'
                      : 'error',
            ),
        networkMode: 'always',
    });
}

/** A job's rows, a page at a time through the runner's cursor, filtered by outcome on the runner. */
export function useBulkJobRows(
    target: ClientTarget,
    moduleId: string,
    job: JobHeader | undefined,
    outcome: string,
) {
    const id = formatTarget(target) ?? 'unaddressable';
    const reportId = job?.reportId ?? '';
    return useInfiniteQuery({
        // `finishedAt` in the key: a job that finished re-reads its rows once.
        queryKey: [...bulkKeys.rows(id, moduleId, reportId, outcome), job?.finishedAt ?? null],
        queryFn: ({ signal, pageParam }) =>
            getBulkJob(
                target,
                moduleId,
                reportId,
                {
                    limit: TABLE_PAGE_SIZE,
                    cursor: pageParam,
                    outcome: outcome === 'all' ? undefined : outcome,
                },
                signal,
            ),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (page) => page.nextCursor ?? undefined,
        enabled: job !== undefined && !isActive(job.state),
        retry: retryUnlessClientError,
    });
}

/** Every row of a job (optionally one outcome), read page by page. For the download and the launch. */
export function readAllRows(
    target: ClientTarget,
    moduleId: string,
    reportId: string,
    outcome?: string,
): Promise<TenantRow[]> {
    return collectAllRows(async (cursor) => {
        const page = await getBulkJob(target, moduleId, reportId, {
            limit: FULL_READ_PAGE_SIZE,
            cursor,
            outcome,
        });
        return { rows: page.rows, nextCursor: page.nextCursor };
    });
}

function useInvalidateJobs(target: ClientTarget, moduleId: string) {
    const queryClient = useQueryClient();
    const id = formatTarget(target) ?? 'unaddressable';
    return () => queryClient.invalidateQueries({ queryKey: bulkKeys.jobs(id, moduleId) });
}

/** Writes are never retried by the client: a second POST is the operator's decision. */
export function useStartDryRun(target: ClientTarget, moduleId: string) {
    const invalidate = useInvalidateJobs(target, moduleId);
    return useMutation({
        mutationFn: (input: DryRunInput) => startBulkDryRun(target, moduleId, input),
        onSettled: invalidate,
        retry: false,
        networkMode: 'always',
    });
}

export function useLaunch(target: ClientTarget, moduleId: string, dryRunReportId: string) {
    const invalidate = useInvalidateJobs(target, moduleId);
    return useMutation({
        mutationFn: (input: { readonly reason: string; readonly tenantIds: readonly string[] }) =>
            launchBulkMigration(target, moduleId, dryRunReportId, input),
        onSettled: invalidate,
        retry: false,
        networkMode: 'always',
    });
}

export function useRefresh(target: ClientTarget, moduleId: string, launchReportId: string) {
    const invalidate = useInvalidateJobs(target, moduleId);
    return useMutation({
        mutationFn: () => refreshBulkLaunch(target, moduleId, launchReportId),
        onSettled: invalidate,
        retry: false,
        networkMode: 'always',
    });
}

/**
 * Asks for a refresh whenever `autoRefreshDue` says so, checking every 5 s.
 * Stops on its own when no run is live or the reader may not refresh.
 */
export function useAutoRefresh(input: {
    readonly canRefresh: boolean;
    readonly launch: JobHeader | undefined;
    readonly latestReport: JobHeader | undefined;
    readonly refresh: ReturnType<typeof useRefresh>;
}): void {
    const { canRefresh, launch, latestReport, refresh } = input;
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const timer = setInterval(() => {
            setNow(Date.now());
        }, 5000);
        return () => {
            clearInterval(timer);
        };
    }, []);

    const due =
        launch !== undefined &&
        refresh.error === null &&
        autoRefreshDue({ canRefresh, launch, latestReport, refreshing: refresh.isPending, now });

    // `mutate` is stable across renders; only `due` turning true fires it.
    const { mutate } = refresh;
    useEffect(() => {
        if (due) mutate();
    }, [due, mutate]);
}

/** `?module=&report=&view=`, beside the `?env=&slot=` the target hook owns. */
export function useMigrationLocation() {
    const [params, setParams] = useSearchParams();
    const moduleId = params.get('module');
    const reportId = params.get('report');
    const view = params.get('view');

    const write = (next: {
        module?: string | null;
        report?: string | null;
        view?: string | null;
    }) => {
        setParams((current) => {
            const updated = new URLSearchParams(current);
            for (const [key, value] of Object.entries(next)) {
                if (value === undefined) continue;
                if (value === null) updated.delete(key);
                else updated.set(key, value);
            }
            return updated;
        });
    };

    return {
        moduleId,
        reportId,
        view,
        openModule: (id: string | null) => {
            write({ module: id, report: null, view: null });
        },
        /** Opens a job at its own step; `keepView` keeps `?view=` (a redirect, not a choice). */
        openReport: (id: string | null, keepView = false) => {
            write(keepView ? { report: id } : { report: id, view: null });
        },
        openFinalReport: (launchReportId: string) => {
            write({ report: launchReportId, view: FINAL_REPORT_VIEW });
        },
    };
}
